import { FieldError } from "react-hook-form";
import { INPUT_TYPES } from "@miplatform/constants";
import {
  BaseTextFieldProps,
  FilledInputProps,
  InputLabelProps,
  SlotProps,
  TextField,
  TextFieldPropsSizeOverrides,
} from "@mui/material";

export const MiInputTextbox: React.FC<MiInputTextboxProps> = ({
  type = "text",
  handleChange,
  onChange,
  testID,
  value,
  label,
  error,
  name,
  disabled,
  readOnly,
  required,
  style,
  inputProps,
  textfieldProps,
  sx = {},
  multiline = false,
  rows,
  maxRows,
  minRows,
  className,
  onBlur,
  helperText = null,
  placeholder = "--",
  shrink,
  size = "small",
  inputLabelProps = {},
  textTruncate = true,
}) => {
  const isShrink = !!value || shrink || !!readOnly || type === INPUT_TYPES.TIME;
  const placeholderText = !readOnly && !!label ? "" : placeholder;

  return (
    <TextField
      value={value ?? ""}
      onChange={handleChange ?? onChange}
      onBlur={onBlur}
      error={!!error}
      helperText={error instanceof Object ? error.message : helperText}
      placeholder={placeholderText}
      size={size as any}
      className={className}
      type={type}
      fullWidth
      slotProps={{
        root: {
          /* @ts-expect-error readOnly somehow has to be here */
          readOnly,
        },
        input: {
          readOnly,
          ...textfieldProps,
          inputProps: {
            "data-testid": `mi-input-text-box-${testID ?? name ?? label}`,
            ...inputProps,
          },
        },
        inputLabel: {
          ...inputLabelProps,
          shrink: isShrink,
        },
      }}
      style={style}
      disabled={disabled}
      label={label}
      sx={{
        ...sx,
        "& .MuiInputBase-input": {
          ...(textTruncate && { textOverflow: "ellipsis" }),
        },
      }}
      required={required}
      multiline={multiline}
      rows={rows}
      maxRows={maxRows}
      minRows={minRows}
    />
  );
};

interface MiInputTextboxProps {
  type?: string;
  testID?: string;
  handleChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  value?: string | number;
  label?: string;
  error?: { message: string } | string | boolean | null | FieldError;
  name?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  style?: React.CSSProperties;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  inputLabelProps?: Partial<InputLabelProps>;
  textfieldProps?: SlotProps<
    React.ElementType<FilledInputProps, keyof React.JSX.IntrinsicElements>,
    unknown,
    BaseTextFieldProps
  >;
  sx?: object;
  multiline?: boolean;
  rows?: number;
  maxRows?: number;
  minRows?: number;
  className?: string;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  helperText?: string | null;
  placeholder?: string;
  shrink?: boolean;
  size?: "small" | "medium" | TextFieldPropsSizeOverrides;
  textTruncate?: boolean;
}
